"use client";

import { FacilityDetailsResponse } from "@features/ax/assetRegistry/facilities/types";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { useToast } from "@shared/hooks/use-toast";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Network, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useNetworksList } from "../../../../hooks";
import { NetworkFilters, NetworkItem } from "../../../../types";
import { NetworkSearchModal } from "./networkSearchModal";
import { NetworkSuggestionsModal } from "./networkSuggestionModal";

interface NetworksTabProps {
  facility: FacilityDetailsResponse;
}

const NetworksTab: React.FC<NetworksTabProps> = ({ facility }) => {
  const [showNetworkSuggestionsModal, setShowNetworkSuggestionsModal] =
    useState(false);
  const [showNetworkSearchModal, setShowNetworkSearchModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();
  const itemsPerPage = PAGE_SIZE;
  const navigate = useNavigate();

  useEffect(() => {
    logEvent("networks_tab_viewed");
  }, []);

  const getFiltersFromUrl = useCallback((): NetworkFilters => {
    const urlFilters: NetworkFilters = {
      page: currentPage,
      limit: itemsPerPage,
      facilityId: facility.id,
    };

    const queryParam = searchParams.get("search");
    if (queryParam) {
      urlFilters.search = queryParam;
    }

    const networkTypeParam = searchParams.get("network_type");
    if (networkTypeParam && networkTypeParam !== "all") {
      urlFilters.networkType = networkTypeParam.split(",");
    }

    const statusParam = searchParams.get("operational_status");
    if (statusParam && statusParam !== "all") {
      urlFilters.operationalStatus = statusParam.split(",").map(Number);
    }

    return urlFilters;
  }, [searchParams, facility.id, itemsPerPage, currentPage]);

  const filters = useMemo(() => getFiltersFromUrl(), [getFiltersFromUrl]);

  const { data } = useNetworksList(filters);

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  const handleSearch = useCallback(
    (searchTerm: string, paramName: string) => {
      const newParams = new URLSearchParams(searchParams);

      if (searchTerm.trim()) {
        newParams.set(paramName, searchTerm.trim());
      } else {
        newParams.delete(paramName);
      }

      newParams.set("page", "1");
      setCurrentPage(1);

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());

      setSearchParams(newParams, { replace: true });
      setCurrentPage(newPage);
    },
    [searchParams, setSearchParams]
  );

  const getOperationalStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "inactive":
        return "bg-red-100 text-red-800 border-red-200";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const networksColumns: TableColumn<NetworkItem>[] = [
    {
      key: "networkNo",
      header: "Network ID",
      type: "text",
      headerTooltip: "The unique identifier for this network.",
    },
    {
      key: "networkName",
      header: "Name",
      type: "text",
      headerTooltip: "The common name used to identify this network.",
    },
    {
      key: "networkType",
      header: "Type",
      headerTooltip:
        "The classification of this network (e.g., Distribution, Transmission, Service).",
    },
    {
      key: "operationalStatusDisplay",
      header: "Status",
      type: "custom",
      render: (value: string) => (
        <Badge className={getOperationalStatusColor(value)} variant="secondary">
          {value}
        </Badge>
      ),
      headerTooltip: "The current operational status of the network.",
    },
    {
      key: "designCapacity",
      header: "Design Capacity",
      type: "text",
      headerTooltip: "The maximum design capacity of the network.",
    },
    {
      key: "customerConnections",
      header: "Connections",
      type: "text",
      headerTooltip:
        "The total number of customer connections served by this network. This is a key indicator of the network's impact.",
    },
  ];

  const networksActions: TableAction[] = [
    {
      label: "View Details",
      onClick: (row: NetworkItem) => {
        navigate(`/ax/asset-registry/networks/view/${row.id}`);
      },
      icon: "Eye",
      variant: "outline",
    },
  ];

  const handleConnectNetworkSuggestions = (networkIds: string[]) => {
    setShowNetworkSuggestionsModal(false);
    toast({
      title: "Networks Connected",
      description: `Successfully connected ${networkIds.length} network(s) to ${facility.facilityName}`,
    });
  };

  const handleSkipToNetworkSearch = () => {
    setShowNetworkSuggestionsModal(false);
    setShowNetworkSearchModal(true);
  };

  const handleConnectFromNetworkSearch = (networkIds: string[]) => {
    setShowNetworkSearchModal(false);
    toast({
      title: "Networks Connected",
      description: `Successfully connected ${networkIds.length} network(s) to ${facility.facilityName}`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
            <Network className="h-5 w-5 text-indigo-600" />
          </div>
          <PageHeader
            title="Networks Management"
            subtitle={`Monitor and manage distribution networks within ${facility.facilityName}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              navigate("/ax/asset-registry/networks/create/add");
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Network
          </Button>
        </div>
      </div>

      <Card>
        <CardContent>
          <GenericTable
            data={data?.results || []}
            columns={networksColumns}
            rowKey="id"
            actions={networksActions}
            actionsType="dropdown"
            searchPlaceholder="Search networks by ID, name..."
            searchParamName="search"
            searchDebounceMs={300}
            onSearch={handleSearch}
            pagination={pagination}
            onPageChange={handlePageChange}
            emptyMessage="No networks found for this facility"
            emptyIcon={<Network className="h-8 w-8 text-gray-400" />}
          />
        </CardContent>
      </Card>

      <NetworkSuggestionsModal
        isOpen={showNetworkSuggestionsModal}
        onClose={() => setShowNetworkSuggestionsModal(false)}
        facilityName={facility.facilityName}
        onConnectSuggestions={handleConnectNetworkSuggestions}
        onSkipToSearch={handleSkipToNetworkSearch}
      />

      <NetworkSearchModal
        isOpen={showNetworkSearchModal}
        onClose={() => setShowNetworkSearchModal(false)}
        facilityName={facility.facilityName}
        onConnectNetworks={handleConnectFromNetworkSearch}
      />
    </div>
  );
};

export default NetworksTab;
